import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { EntitlementService } from '@cosmos/angular/utils';
import { FEATURE_ID } from '@cosmos/shared';

export type AiButtonSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'cosmos-ai-button',
  templateUrl: './ai-button.component.html',
  styleUrl: './ai-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, TooltipModule],
})
export class AiButtonComponent {
  private readonly entitlementService = inject(EntitlementService);

  /** Text describing the action, e.g. "Summarize data". The button appends "with CoSMoS AI". */
  readonly actionText = input.required<string>();

  /**
   * FMS feature key that gates this button. If the user lacks this feature the button
   * is locked (padlock icon + tooltip). No key = always locked.
   */
  readonly featureKey = input<FEATURE_ID | undefined>(undefined);

  readonly size = input<AiButtonSize>('md');

  /** When true, applies the PRO gradient styling regardless of lock state. Pure visual. */
  readonly pro = input<boolean>(false);

  /** Emitted when the button is clicked and is not locked */
  readonly clicked = output<void>();

  readonly isLocked = computed(() => {
    const key = this.featureKey();
    if (!key) return true;
    // Derive the module from the first segment of the feature key
    const module = key.split('.')[0] as any;
    return !this.entitlementService.hasFeature(module, key);
  });

  readonly hostClasses = computed(() => ({
    'ai-btn': true,
    [`ai-btn--${this.size()}`]: true,
    'ai-btn--pro': this.pro(),
    'ai-btn--locked': this.isLocked(),
  }));

  readonly tooltipText = computed(() =>
    this.isLocked() ? 'ai.pro-locked-tooltip' : '',
  );

  onClick(): void {
    if (!this.isLocked()) {
      this.clicked.emit();
    }
  }
}
